import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactJson from 'react-json-view';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  TablePagination,
  Button,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

function FdiRepositoryExplorer({ apiBaseUrl }) {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [endpoint, setEndpoint] = useState('/edds');
  const [selectedProtocol, setSelectedProtocol] = useState('');
  const [selectedManufacturerId, setSelectedManufacturerId] = useState('');
  const [selectedDeviceType, setSelectedDeviceType] = useState('');
  const [protocols, setProtocols] = useState([]);
  const [manufacturerIds, setManufacturerIds] = useState([]);
  const [sortedManufacturerIds, setSortedManufacturerIds] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const [devinfoExpanded, setDevinfoExpanded] = useState(null);
  const [devinfoContents, setDevinfoContents] = useState(null);
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [manufacturerNames, setManufacturerNames] = useState({});
  const [allManufacturerData, setAllManufacturerData] = useState([]);
  const [manufacturerNamesByProtocol, setManufacturerNamesByProtocol] = useState({});
  const [deviceTypeNamesByProtocolAndManufacturer, setDeviceTypeNamesByProtocolAndManufacturer] = useState({});
  const [deviceTypeData, setDeviceTypeData] = useState([]);
  const [sortOrder, setSortOrder] = useState('id');
  const [isManager, setIsManager] = useState(false);
  const [recordStatusFilter, setRecordStatusFilter] = useState('all');
  const [rangeEnabled, setRangeEnabled] = useState(false);
  const [sinceDate, setSinceDate] = useState('');
  const [untilDate, setUntilDate] = useState('');

  const getAccessToken = async () => {
    if (accounts.length === 0) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['api://3923e780-27fe-4174-a906-4659370dcd85/.default'],
        account: accounts[0],
      });
      const decoded = jwtDecode(response.accessToken);
      const roles = decoded.roles || [];
      const hasManagerRole = roles.includes('repository.manager');
      setIsManager(hasManagerRole);
      return response.accessToken;
    } catch (err) {
      console.error('Error acquiring token:', err);
      instance.acquireTokenRedirect({ scopes: ['api://3923e780-27fe-4174-a906-4659370dcd85/.default'] });
      return null;
    }
  };

  const fetchExternalData = async () => {
    const timestamp = new Date().getTime();
    try {
      console.log('Fetching manufacturer IDs from:', `https://assets.fieldcommgroup.org/public/registry/production/id/manufacturerids.json?_t=${timestamp}`); 
      const response = await axios.get(`https://assets.fieldcommgroup.org/public/registry/production/id/manufacturerids.json?_t=${timestamp}`);
      const manufacturerData = response.data.manufacturerIds || [];
      setAllManufacturerData(manufacturerData);
      const mappingByProtocol = manufacturerData.reduce((acc, item) => {
        const protocol = item.protocol.toLowerCase();
        if (!acc[protocol]) acc[protocol] = {};
        acc[protocol][item.manufacturerId.toLowerCase()] = item.manufacturerString;
        return acc;
      }, {});
      setManufacturerNamesByProtocol(mappingByProtocol);
    } catch (err) {
      console.error('Error fetching manufacturer IDs:', err);
      setError('Failed to load manufacturer names. Displaying IDs only.');
    }
    try {
      console.log('Fetching device type IDs from:', `https://assets.fieldcommgroup.org/public/registry/production/id/devicetypes.json?_t=${timestamp}`);
      const response = await axios.get(`https://assets.fieldcommgroup.org/public/registry/production/id/devicetypes.json?_t=${timestamp}`); 
      const dtData = response.data.deviceTypes || [];
      setDeviceTypeData(dtData);
      const mappingByProtocolAndManufacturer = dtData.reduce((acc, item) => {
        const protocol = item.protocol.toLowerCase();
        const manufacturerId = item.manufacturerId.toLowerCase();
        const deviceTypeId = item.deviceTypeId.toLowerCase();
        if (!acc[protocol]) acc[protocol] = {};
        if (!acc[protocol][manufacturerId]) acc[protocol][manufacturerId] = {};
        acc[protocol][manufacturerId][deviceTypeId] = item.deviceTypeIdString;
        return acc;
      }, {});
      setDeviceTypeNamesByProtocolAndManufacturer(mappingByProtocolAndManufacturer);
    } catch (err) {
      console.error('Error fetching device type IDs:', err);
      setError('Failed to load device type names. Displaying IDs only.');
    }
  };

  useEffect(() => {
    fetchExternalData();
  }, []);

  useEffect(() => {
    if (!selectedProtocol || Object.keys(manufacturerNamesByProtocol).length === 0) {
      setManufacturerNames({});
      return;
    }
    const mapping = manufacturerNamesByProtocol[selectedProtocol.toLowerCase()] || {};
    setManufacturerNames(mapping);
  }, [selectedProtocol, manufacturerNamesByProtocol]);

  useEffect(() => {
    if (manufacturerIds.length === 0) {
      setSortedManufacturerIds([]);
      return;
    }
    const sorted = [...manufacturerIds].sort((a, b) => {
      if (sortOrder === 'id') {
        return parseInt(a.name, 16) - parseInt(b.name, 16);
      } else {
        const nameA = manufacturerNames[a.name.toLowerCase()] || a.name;
        const nameB = manufacturerNames[b.name.toLowerCase()] || b.name;
        return nameA.localeCompare(nameB);
      }
    });
    setSortedManufacturerIds(sorted);
  }, [manufacturerIds, sortOrder, manufacturerNames]);

  useEffect(() => {
    fetchProtocols();
  }, [apiBaseUrl]);

  useEffect(() => {
    if ((endpoint === '/edds' || endpoint === '/packages' || endpoint === '/devinfos') && selectedProtocol) {
      fetchManufacturerIds();
    } else {
      setManufacturerIds([]);
      setSelectedManufacturerId('');
      setDeviceTypes([]);
      setSelectedDeviceType('');
    }
  }, [endpoint, selectedProtocol, apiBaseUrl]);

  useEffect(() => {
    if ((endpoint === '/edds' || endpoint === '/packages' || endpoint === '/devinfos') && selectedProtocol && selectedManufacturerId) {
      fetchDeviceTypes();
    } else {
      setDeviceTypes([]);
      setSelectedDeviceType('');
    }
  }, [endpoint, selectedProtocol, selectedManufacturerId, apiBaseUrl]);

  useEffect(() => {
    fetchData();
  }, [endpoint, selectedProtocol, selectedManufacturerId, selectedDeviceType, page, rowsPerPage, recordStatusFilter, rangeEnabled, sinceDate, untilDate, apiBaseUrl]);

  const fetchProtocols = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      console.log('Fetching protocols from:', `${apiBaseUrl}/protocols`);
      const response = await axios.get(`${apiBaseUrl}/protocols`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      setProtocols(response.data.protocols || []);
    } catch (err) {
      console.error('Error fetching protocols:', err);
      setError('Failed to load protocols. Some features may be limited.');
    }
  };

  const fetchManufacturerIds = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
      console.log('Fetching manufacturer IDs from:', `${apiBaseUrl}/${cleanEndpoint}/protocol/${selectedProtocol}/manufacturerIds`);
      const response = await axios.get(`${apiBaseUrl}/${cleanEndpoint}/protocol/${selectedProtocol}/manufacturerIds`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      setManufacturerIds(response.data.manufacturers || []);
    } catch (err) {
      console.error('Error fetching manufacturerIds:', err);
      setError('Failed to load manufacturer IDs. Filtering by manufacturer may not work.');
      setManufacturerIds([]);
    }
  };

  const fetchDeviceTypes = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
      console.log('Fetching device types from:', `${apiBaseUrl}/${cleanEndpoint}/protocol/${selectedProtocol}/manufacturerId/${selectedManufacturerId}`);
      const response = await axios.get(
        `${apiBaseUrl}/${cleanEndpoint}/protocol/${selectedProtocol}/manufacturerId/${selectedManufacturerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );
      const deviceTypeList = response.data.entries.map((entry) => entry.edd?.deviceTypeId || entry.devInfo?.deviceTypeId || entry.deviceTypeId);
      const uniqueSortedList = [...new Set(deviceTypeList)].sort((a, b) => parseInt(a, 16) - parseInt(b, 16));
      setDeviceTypes(uniqueSortedList);
    } catch (err) {
      console.error('Error fetching deviceTypes:', err);
      setError('Failed to load device types. Filtering by device type may not work.');
      setDeviceTypes([]);
    }
  };

  const fetchData = async () => {
    if ((endpoint === '/edds' || endpoint === '/packages' || endpoint === '/devinfos') && !selectedProtocol) {
      setData([]);
      setNextPageToken(null);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let fullEndpoint = endpoint;
      if (endpoint === '/edds' || endpoint === '/packages' || endpoint === '/devinfos') {
        if (selectedProtocol) {
          fullEndpoint += `/protocol/${selectedProtocol}`;
          if (selectedManufacturerId) {
            fullEndpoint += `/manufacturerId/${selectedManufacturerId}`;
            if (selectedDeviceType) {
              fullEndpoint += `/deviceType/${selectedDeviceType}`;
            }
          }
        }
      }
      const token = await getAccessToken();
      if (!token) return;
      const params = {
        pageSize: rowsPerPage,
        pageToken: page > 0 ? nextPageToken : undefined,
      };
      if (endpoint === '/edds' || endpoint === '/packages' || endpoint === '/devinfos') {
        if (recordStatusFilter === 'inactive' || recordStatusFilter === 'all') {
          params.recordStatus = recordStatusFilter;
        }
      }
      if (rangeEnabled && (endpoint === '/edds' || endpoint === '/packages' || endpoint === '/devinfos')) {
        if (sinceDate) {
          params.since = new Date(sinceDate).toISOString();
        }
        if (untilDate) {
          params.until = new Date(untilDate).toISOString();
        }
      }
      console.log('Fetching data from:', `${apiBaseUrl}${fullEndpoint}`, 'with params:', params);
      const response = await axios.get(`${apiBaseUrl}${fullEndpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        params: params,
      });
      let result, newNextPageToken;
      if (endpoint === '/protocols') {
        result = response.data.protocols || [];
        newNextPageToken = null;
      } else {
        result = response.data.entries || [];
        newNextPageToken = response.data.nextPageToken || null;
      }
      setData(result);
      setNextPageToken(newNextPageToken);
      setTotalCount((prev) => (page === 0 ? result.length : prev + result.length));
      setExpandedRow(null);
    } catch (err) {
      console.error('Error fetching data:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
      });
      let errorMessage = 'An error occurred while fetching data.';
      if (err.response) {
        switch (err.response.status) {
          case 400:
            errorMessage = 'Bad Request: Check your parameters.';
            break;
          case 401:
            errorMessage = 'Unauthorized: Please log in again.';
            break;
          case 403:
            errorMessage = 'Forbidden: Insufficient permissions.';
            break;
          case 404:
            errorMessage = 'Not Found: The requested resource does not exist.';
            break;
          case 500:
            errorMessage = 'Server Error: Try again later.';
            break;
          default:
            errorMessage = `Error ${err.response.status}: ${err.response.statusText}`;
        }
        if (err.response.data?.fault?.faultstring) {
          errorMessage += ` - ${err.response.data.fault.faultstring}`;
        }
      } else if (err.request) {
        errorMessage = 'Network Error: Check your connection or the API server.';
      }
      setError(errorMessage);
      setData([]);
      setNextPageToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDevinfo = async (index, item) => {
    if (devinfoExpanded === index) {
      setDevinfoExpanded(null);
      setDevinfoContents(null);
      return;
    }
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) return;
      const url = `${apiBaseUrl}/devinfos/protocol/${item.devInfo.protocol}/manufacturerId/${item.devInfo.manufacturerId}/deviceType/${item.devInfo.deviceTypeId}/deviceRevision/${item.devInfo.deviceRevision}/sdiVersion/2`;
      console.log('Fetching devinfo from:', url);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      setDevinfoContents(response.data);
      setDevinfoExpanded(index);
    } catch (err) {
      console.error('Error fetching devinfo:', err);
      setError('Failed to fetch devinfo JSON.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      if (!token) return;
      let mediaUrl;
      if (endpoint === '/edds') {
        mediaUrl = `${apiBaseUrl}/edds/protocol/${item.edd.protocol}/manufacturerId/${item.edd.manufacturerId}/deviceType/${item.edd.deviceTypeId}/deviceRevision/${item.edd.deviceRevision}/mediaUrl`;
      } else if (endpoint === '/devinfos') {
        mediaUrl = `${apiBaseUrl}/devinfos/protocol/${item.devInfo.protocol}/manufacturerId/${item.devInfo.manufacturerId}/deviceType/${item.devInfo.deviceTypeId}/deviceRevision/${item.devInfo.deviceRevision}/mediaUrl`;
      } else if (endpoint === '/packages') {
        mediaUrl = `${apiBaseUrl}/packages/package/${item.catalog.packageId}/version/${item.catalog.version}/mediaUrl`;
      } else {
        throw new Error('Download not supported for this endpoint');
      }
      console.log('Fetching media URL from:', mediaUrl);
      const urlResponse = await axios.get(mediaUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      const downloadUrl = urlResponse.data.url;
      if (!downloadUrl) {
        throw new Error('No download URL provided in response');
      }
      console.log('Downloading file from:', downloadUrl); 
      const fileResponse = await axios.get(downloadUrl, {
        responseType: 'blob',
      });
      let filename;
      const contentDisposition = fileResponse.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      if (!filename) {
        const urlParts = downloadUrl.split('/');
        filename = urlParts[urlParts.length - 1].split('?')[0] ||
          (endpoint === '/edds' ? 'edd_file.zip' :
           endpoint === '/devinfos' ? 'devinfo_file.zip' : 'package_file.fdix');
      }
      const blob = new Blob([fileResponse.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download Error:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        headers: err.response?.headers,
      });
      setError('Failed to download file. Check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      if (!token) return;
      let deleteUrl;
      if (endpoint === '/edds') {
        deleteUrl = `${apiBaseUrl}/edds/protocol/${itemToDelete.edd.protocol}/manufacturerId/${itemToDelete.edd.manufacturerId}/deviceType/${itemToDelete.edd.deviceTypeId}/deviceRevision/${itemToDelete.edd.deviceRevision}`;
      } else if (endpoint === '/packages') {
        deleteUrl = `${apiBaseUrl}/packages/package/${itemToDelete.catalog.packageId}/version/${itemToDelete.catalog.version}`;
      } else if (endpoint === '/devinfos') {
        deleteUrl = `${apiBaseUrl}/devinfos/protocol/${itemToDelete.devInfo.protocol}/manufacturerId/${itemToDelete.devInfo.manufacturerId}/deviceType/${itemToDelete.devInfo.deviceTypeId}/deviceRevision/${itemToDelete.devInfo.deviceRevision}`;
      } else {
        throw new Error('Delete not supported for this endpoint');
      }
      await axios.delete(deleteUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (err) {
      console.error('Delete Error:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
      });
      setError(`Failed to delete item: ${err.message}`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleStatusChange = async (item, newStatus) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      if (!token) return;
      let patchUrl;
      if (endpoint === '/edds') {
        patchUrl = `${apiBaseUrl}/edds/protocol/${item.edd.protocol}/manufacturerId/${item.edd.manufacturerId}/deviceType/${item.edd.deviceTypeId}/deviceRevision/${item.edd.deviceRevision}`;
      } else if (endpoint === '/packages') {
        patchUrl = `${apiBaseUrl}/packages/package/${item.catalog.packageId}/version/${item.catalog.version}`;
      } else if (endpoint === '/devinfos') {
        patchUrl = `${apiBaseUrl}/devinfos/protocol/${item.devInfo.protocol}/manufacturerId/${item.devInfo.manufacturerId}/deviceType/${item.devInfo.deviceTypeId}/deviceRevision/${item.devInfo.deviceRevision}`;
      } else {
        throw new Error('Status update not supported for this endpoint');
      }
      await axios.patch(patchUrl, {
        recordStatus: newStatus,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      fetchData();
    } catch (err) {
      console.error('Status Update Error:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
      });
      setError(`Failed to update status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleDetails = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const handleEndpointChange = (event) => {
    setEndpoint(event.target.value);
    setPage(0);
    setNextPageToken(null);
    setSelectedProtocol('');
    setSelectedManufacturerId('');
    setSelectedDeviceType('');
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
    setDeleteEnabled(false);
    setRecordStatusFilter('all');
    setRangeEnabled(false);
    setSinceDate('');
    setUntilDate('');
  };

  const handleProtocolChange = (event) => {
    setSelectedProtocol(event.target.value);
    setPage(0);
    setNextPageToken(null);
    setSelectedManufacturerId('');
    setSelectedDeviceType('');
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
  };

  const handleManufacturerIdChange = (event) => {
    setSelectedManufacturerId(event.target.value);
    setPage(0);
    setNextPageToken(null);
    setSelectedDeviceType('');
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
  };

  const handleDeviceTypeChange = (event) => {
    setSelectedDeviceType(event.target.value);
    setPage(0);
    setNextPageToken(null);
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setNextPageToken(null);
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
  };

  const handleSortOrderChange = (event) => {
    setSortOrder(event.target.value);
  };

  const handleRecordStatusFilterChange = (event) => {
    setRecordStatusFilter(event.target.value);
    setPage(0);
    setNextPageToken(null);
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
  };

  const handleRangeEnabledChange = (event) => {
    setRangeEnabled(event.target.checked);
    setPage(0);
    setNextPageToken(null);
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
    if (!event.target.checked) {
      setSinceDate('');
      setUntilDate('');
    }
  };

  const handleSinceDateChange = (event) => {
    setSinceDate(event.target.value);
    setPage(0);
    setNextPageToken(null);
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
  };

  const handleUntilDateChange = (event) => {
    setUntilDate(event.target.value);
    setPage(0);
    setNextPageToken(null);
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
  };

  const handleReset = () => {
    setSelectedProtocol('');
    setSelectedManufacturerId('');
    setSelectedDeviceType('');
    setPage(0);
    setNextPageToken(null);
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
    setDeleteEnabled(false);
    setData([]);
    setTotalCount(0);
    setRecordStatusFilter('active');
    setRangeEnabled(false);
    setSinceDate('');
    setUntilDate('');
  };

  const handleRefresh = () => {
    setPage(0);
    setNextPageToken(null);
    setExpandedRow(null);
    setDevinfoExpanded(null);
    setDevinfoContents(null);
    setTotalCount(0);
    fetchExternalData();
    fetchData();
  };

  const getManufacturerName = (obj) => {
    if (!obj || !obj.manufacturerId) return '⚠️ Unknown';
    const protocolLower = obj.protocol ? obj.protocol.toLowerCase() : '';
    const idLower = obj.manufacturerId.toLowerCase();
    // Rule 1: If API returns empty or undefined manufacturerIdString, start with ⚠️
    if (!obj.manufacturerIdString || obj.manufacturerIdString.trim() === '') {
      const mapping = manufacturerNamesByProtocol[protocolLower] || {};
      let name = mapping[idLower] || 'Unknown';
      const isHart = protocolLower === 'hart';
      let viaExpanded = false;
      if (isHart && idLower.startsWith('0000e') && obj.deviceTypeId) {
        const manufSuffixInt = parseInt(idLower.substring(4, 6), 16);
        if (manufSuffixInt >= 0xe0 && manufSuffixInt <= 0xef) {
          const deviceTypeIdLower = obj.deviceTypeId.toLowerCase();
          const expandedDeviceTypeId = `${idLower.slice(-2)}${deviceTypeIdLower.slice(-2)}`;
          const match = deviceTypeData.find(item =>
            item.protocol.toLowerCase() === protocolLower &&
            item.deviceTypeId.toLowerCase() === expandedDeviceTypeId
          );
          if (match && match.manufacturerIdString) {
            name = `⚠️ ${match.manufacturerIdString} (${match.manufacturerId}) ↔️ (${idLower})`;
            viaExpanded = true;
          } else {
            name = `⚠️ Unknown (${idLower})`;
          }
        }
      }
      if (!viaExpanded) {
        name = name === 'Unknown' ? `⚠️ Unknown (${idLower})` : `⚠️ ${name} (${idLower})`;
      }
      return name;
    }
    // If API provides a valid manufacturerIdString, use it without ⚠️
    return `${obj.manufacturerIdString} (${obj.manufacturerId})`;
  };

  const getDeviceTypeName = (deviceTypeId, itemProtocol, manufacturerId) => {
    if (!deviceTypeId || !itemProtocol || !manufacturerId) {
      return { name: '⚠️ Unknown', viaExpanded: false };
    }
    const protocolLower = itemProtocol.toLowerCase();
    const manufacturerIdLower = manufacturerId.toLowerCase();
    const deviceTypeIdLower = deviceTypeId.toLowerCase();
    const protocolMapping = deviceTypeNamesByProtocolAndManufacturer[protocolLower] || {};
    const manufacturerMapping = protocolMapping[manufacturerIdLower] || {};
    let viaExpanded = false;
    let name = manufacturerMapping[deviceTypeIdLower] || 'Unknown';
    if (protocolLower === 'hart' && deviceTypeIdLower.startsWith('00')) {
      const manufPrefix = manufacturerIdLower.substring(0, 5);
      const manufSuffixInt = parseInt(manufacturerIdLower.substring(4, 6), 16);
      if (manufPrefix === '0000e' && manufSuffixInt >= 0xe0 && manufSuffixInt <= 0xef) {
        const expandedDeviceTypeId = `${manufacturerIdLower.slice(-2)}${deviceTypeIdLower.slice(-2)}`;
        const match = deviceTypeData.find(item =>
          item.protocol.toLowerCase() === protocolLower &&
          item.deviceTypeId.toLowerCase() === expandedDeviceTypeId
        );
        if (match) {
          name = `${match.deviceTypeIdString} (${deviceTypeIdLower}) ↔️ (${match.deviceTypeId})`;
          viaExpanded = true;
        } else {
          name = `Unknown (${deviceTypeIdLower})`;
        }
      }
    }
    if (!viaExpanded) {
      name = name === 'Unknown' ? `Unknown (${deviceTypeIdLower})` : `${name} (${deviceTypeIdLower})`;
    }
    return { name, viaExpanded };
  };

  const getDeviceTypeNameFromObj = (obj) => {
    if (!obj || !obj.deviceTypeId) return { name: '⚠️ Unknown', viaExpanded: false };
    // Rule 1: If API returns empty or undefined deviceTypeIdString, start with ⚠️
    if (!obj.deviceTypeIdString || obj.deviceTypeIdString.trim() === '') {
      const { name, viaExpanded } = getDeviceTypeName(obj.deviceTypeId, obj.protocol, obj.manufacturerId);
      return { name: `⚠️ ${name}`, viaExpanded };
    }
    // If API provides a valid deviceTypeIdString, use it without ⚠️
    return { name: `${obj.deviceTypeIdString} (${obj.deviceTypeId})`, viaExpanded: false };
  };

  return (
    <Container style={{ marginTop: '20px', marginLeft: 0 }} maxWidth={false} sx={{ width: '100%', maxWidth: '1600px' }}>
      <Typography variant="h4" gutterBottom>
        Explorer
      </Typography>
      <Toolbar sx={{ justifyContent: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Button
          variant="contained"
          color={deleteEnabled ? 'secondary' : 'primary'}
          onClick={() => setDeleteEnabled(!deleteEnabled)}
          disabled={loading || !isManager}
          sx={{ mr: 2 }}
        >
          {deleteEnabled ? 'Disable Edit' : 'Enable Edit'}
        </Button>
        {(endpoint === '/edds' || endpoint === '/packages' || endpoint === '/devinfos') && (
          <>
            <FormControl sx={{ minWidth: 200, mr: 2 }}>
              <InputLabel>Mfr Order</InputLabel>
              <Select
                value={sortOrder}
                onChange={handleSortOrderChange}
                label="Mfr Order"
              >
                <MenuItem value="id">Id</MenuItem>
                <MenuItem value="string">String</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 200, mr: 2 }}>
              <InputLabel>Show</InputLabel>
              <Select
                value={recordStatusFilter}
                onChange={handleRecordStatusFilterChange}
                label="Show"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rangeEnabled}
                  onChange={handleRangeEnabledChange}
                  color="primary"
                />
              }
              label="Range:"
              sx={{ mr: 2 }}
            />
            <TextField
              label="Since"
              type="datetime-local"
              value={sinceDate}
              onChange={handleSinceDateChange}
              disabled={!rangeEnabled}
              InputLabelProps={{ shrink: true }}
              sx={{ mr: 2, minWidth: 200 }}
            />
            <TextField
              label="Until"
              type="datetime-local"
              value={untilDate}
              onChange={handleUntilDateChange}
              disabled={!rangeEnabled}
              InputLabelProps={{ shrink: true }}
              sx={{ mr: 2, minWidth: 200 }}
            />
          </>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleRefresh}
          disabled={loading}
          startIcon={<RefreshIcon />}
          sx={{ mr: 2 }}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleReset}
          disabled={loading}
        >
          Reset
        </Button>
      </Toolbar>
      {deleteEnabled && apiBaseUrl === 'https://fdirepoproxy.azurewebsites.net/repository/v1' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>WARNING: Production Environment</AlertTitle>
          You are deleting from the PRODUCTION database. Changes here are permanent and affect live data. Proceed with caution!
        </Alert>
      )}
      <FormControl fullWidth style={{ marginBottom: '20px' }}>
        <InputLabel>API Endpoint</InputLabel>
        <Select value={endpoint} onChange={handleEndpointChange}>
          <MenuItem value="/protocols">Protocols</MenuItem>
          <MenuItem value="/edds">EDDs</MenuItem>
          <MenuItem value="/packages">Packages</MenuItem>
          <MenuItem value="/devinfos">DeviceInfos</MenuItem>
        </Select>
      </FormControl>
      {(endpoint === '/edds' || endpoint === '/packages' || endpoint === '/devinfos') && (
        <>
          <FormControl fullWidth style={{ marginBottom: '20px' }}>
            <InputLabel>Protocol</InputLabel>
            <Select
              value={selectedProtocol}
              onChange={handleProtocolChange}
              disabled={protocols.length === 0}
            >
              <MenuItem value="" disabled>
                <em>Select a Protocol</em>
              </MenuItem>
              {protocols.map((protocol) => (
                <MenuItem key={protocol.name} value={protocol.name}>
                  {protocol.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedProtocol && (
            <FormControl fullWidth style={{ marginBottom: '20px' }}>
              <InputLabel>Manufacturer</InputLabel>
              <Select
                value={selectedManufacturerId}
                onChange={handleManufacturerIdChange}
                disabled={sortedManufacturerIds.length === 0}
              >
                <MenuItem value="">
                  <em>All Manufacturers</em>
                </MenuItem>
                {sortedManufacturerIds.map((manufacturer) => (
                  <MenuItem key={manufacturer.name} value={manufacturer.name}>
                    {manufacturerNames[manufacturer.name.toLowerCase()] || manufacturer.name} ({manufacturer.name})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {selectedManufacturerId && (
            <FormControl fullWidth style={{ marginBottom: '20px' }}>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={selectedDeviceType}
                onChange={handleDeviceTypeChange}
                disabled={deviceTypes.length === 0}
              >
                <MenuItem value="">
                  <em>All Device Types</em>
                </MenuItem>
                {deviceTypes.map((deviceType) => {
                  const { name } = getDeviceTypeName(deviceType, selectedProtocol, selectedManufacturerId);
                  return (
                    <MenuItem key={deviceType} value={deviceType}>
                      {name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        </>
      )}
      {error && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          <AlertTitle>Error</AlertTitle>
          {error} — <strong>Check the console for details.</strong>
        </Alert>
      )}
      {loading ? (
        <CircularProgress />
      ) : data.length === 0 && !error ? (
        <Typography>No data available. Please select a protocol to view EDDs, Packages, or DeviceInfos.</Typography>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
            <Table sx={{ tableLayout: 'auto', minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  {endpoint === '/protocols' ? (
                    <>
                      <TableCell>Name</TableCell>
                      <TableCell>Update Time</TableCell>
                      <TableCell>URL</TableCell>
                    </>
                  ) : endpoint === '/edds' || endpoint === '/devinfos' ? (
                    <>
                      <TableCell>Download</TableCell>
                      <TableCell>Update Time</TableCell>
                      <TableCell>Record Status</TableCell>
                      <TableCell>Protocol</TableCell>
                      <TableCell>Manufacturer</TableCell>
                      <TableCell>Device Type</TableCell>
                      <TableCell>Device Revision</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>View Devinfo</TableCell>
                      {deleteEnabled && <TableCell>Status Action</TableCell>}
                      {deleteEnabled && <TableCell>Delete</TableCell>}
                    </>
                  ) : (
                    <>
                      <TableCell>Download</TableCell>
                      <TableCell>Update Time</TableCell>
                      <TableCell>Record Status</TableCell>
                      <TableCell>Package ID</TableCell>
                      <TableCell>Manufacturer</TableCell>
                      <TableCell>Device Type Names</TableCell>
                      <TableCell>Details</TableCell>
                      {deleteEnabled && <TableCell>Status Action</TableCell>}
                      {deleteEnabled && <TableCell>Delete</TableCell>}
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((item, index) => (
                  <React.Fragment key={index}>
                    <TableRow>
                      {endpoint === '/protocols' ? (
                        <>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.updateTime}</TableCell>
                          <TableCell>{item.url}</TableCell>
                        </>
                      ) : endpoint === '/edds' ? (
                        <>
                          <TableCell>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleDownload(item)}
                              disabled={loading}
                            >
                              Download
                            </Button>
                          </TableCell>
                          <TableCell>{item.updateTime}</TableCell>
                          <TableCell sx={{ color: item.recordStatus === 'active' ? 'green' : item.recordStatus === 'inactive' ? 'red' : 'inherit' }}>
                            {item.recordStatus || 'N/A'}
                          </TableCell>
                          <TableCell>{item.edd?.protocol || 'N/A'}</TableCell>
                          <TableCell>{getManufacturerName(item.edd)}</TableCell>
                          <TableCell>{getDeviceTypeNameFromObj(item.edd).name}</TableCell>
                          <TableCell>{item.edd?.deviceRevision || 'N/A'}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={() => toggleDetails(index)}
                              disabled={loading}
                            >
                              {expandedRow === index ? 'Hide Details' : 'Show Details'}
                            </Button>
                          </TableCell>
                          <TableCell></TableCell>
                          {deleteEnabled && (
                            <TableCell>
                              {(!item.recordStatus || item.recordStatus === 'active') ? (
                                <Button
                                  variant="contained"
                                  color="warning"
                                  onClick={() => handleStatusChange(item, 'inactive')}
                                  disabled={loading}
                                >
                                  Deactivate
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleStatusChange(item, 'active')}
                                  disabled={loading}
                                >
                                  Activate
                                </Button>
                              )}
                            </TableCell>
                          )}
                          {deleteEnabled && (
                            <TableCell>
                              <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleDeleteClick(item)}
                                disabled={loading}
                              >
                                Delete
                              </Button>
                            </TableCell>
                          )}
                        </>
                      ) : endpoint === '/devinfos' ? (
                        <>
                          <TableCell>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleDownload(item)}
                              disabled={loading}
                            >
                              Download
                            </Button>
                          </TableCell>
                          <TableCell>{item.updateTime}</TableCell>
                          <TableCell sx={{ color: item.recordStatus === 'active' ? 'green' : item.recordStatus === 'inactive' ? 'red' : 'inherit' }}>
                            {item.recordStatus || 'N/A'}
                          </TableCell>
                          <TableCell>{item.devInfo?.protocol || 'N/A'}</TableCell>
                          <TableCell>{getManufacturerName(item.devInfo)}</TableCell>
                          <TableCell>{getDeviceTypeNameFromObj(item.devInfo).name}</TableCell>
                          <TableCell>{item.devInfo?.deviceRevision || 'N/A'}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={() => toggleDetails(index)}
                              disabled={loading}
                            >
                              {expandedRow === index ? 'Hide Details' : 'Show Details'}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={() => handleViewDevinfo(index, item)}
                              disabled={loading}
                            >
                              {devinfoExpanded === index ? 'Hide Devinfo' : 'View Devinfo'}
                            </Button>
                          </TableCell>
                          {deleteEnabled && (
                            <TableCell>
                              {(!item.recordStatus || item.recordStatus === 'active') ? (
                                <Button
                                  variant="contained"
                                  color="warning"
                                  onClick={() => handleStatusChange(item, 'inactive')}
                                  disabled={loading}
                                >
                                  Deactivate
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleStatusChange(item, 'active')}
                                  disabled={loading}
                                >
                                  Activate
                                </Button>
                              )}
                            </TableCell>
                          )}
                          {deleteEnabled && (
                            <TableCell>
                              <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleDeleteClick(item)}
                                disabled={loading}
                              >
                                Delete
                              </Button>
                            </TableCell>
                          )}
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleDownload(item)}
                              disabled={loading}
                            >
                              Download
                            </Button>
                          </TableCell>
                          <TableCell>{item.updateTime}</TableCell>
                          <TableCell sx={{ color: item.recordStatus === 'active' ? 'green' : item.recordStatus === 'inactive' ? 'red' : 'inherit' }}>
                            {item.recordStatus || 'N/A'}
                          </TableCell>
                          <TableCell>{item.catalog?.packageId || 'N/A'}</TableCell>
                          <TableCell>
                            {item.catalog?.manufacturerName || getManufacturerName({manufacturerId: item.catalog?.manufacturerId, protocol: selectedProtocol || item.catalog?.protocol || '', manufacturerIdString: undefined})}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {item.catalog?.listOfDeviceTypes
                              ?.map((deviceTypeObj) => {
                                const deviceTypeId = deviceTypeObj.deviceType?.deviceTypeId;
                                if (!deviceTypeId) return 'N/A';
                                const dtObj = {
                                  ...deviceTypeObj.deviceType,
                                  protocol: selectedProtocol || item.catalog?.protocol || '',
                                  manufacturerId: item.catalog?.manufacturerId || ''
                                };
                                const { name } = getDeviceTypeNameFromObj(dtObj);
                                return name;
                              })
                              .join(', ') || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={() => toggleDetails(index)}
                              disabled={loading}
                            >
                              {expandedRow === index ? 'Hide Details' : 'Show Details'}
                            </Button>
                          </TableCell>
                          {deleteEnabled && (
                            <TableCell>
                              {(!item.recordStatus || item.recordStatus === 'active') ? (
                                <Button
                                  variant="contained"
                                  color="warning"
                                  onClick={() => handleStatusChange(item, 'inactive')}
                                  disabled={loading}
                                >
                                  Deactivate
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleStatusChange(item, 'active')}
                                  disabled={loading}
                                >
                                  Activate
                                </Button>
                              )}
                            </TableCell>
                          )}
                          {deleteEnabled && (
                            <TableCell>
                              <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleDeleteClick(item)}
                                disabled={loading}
                              >
                                Delete
                              </Button>
                            </TableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                    {endpoint === '/packages' && expandedRow === index && (
                      <TableRow>
                        <TableCell colSpan={deleteEnabled ? 9 : 7} sx={{ backgroundColor: '#1e1e1e', padding: '16px', width: '100%' }}>
                          <Typography variant="h6" gutterBottom color="white">
                            Package Details
                          </Typography>
                          <Box sx={{ width: '100%' }}>
                            <ReactJson
                              src={item}
                              theme="monokai"
                              collapsed={false}
                              displayDataTypes={false}
                              displayObjectSize={false}
                              style={{ backgroundColor: '#1e1e1e', width: '100%' }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {(endpoint === '/edds' || endpoint === '/devinfos') && expandedRow === index && (
                      <TableRow>
                        <TableCell colSpan={deleteEnabled ? 11 : 9} sx={{ backgroundColor: '#1e1e1e', padding: '16px', width: '100%' }}>
                          <Typography variant="h6" gutterBottom color="white">
                            {endpoint === '/edds' ? 'EDD Details' : 'DeviceInfo Details'}
                          </Typography>
                          <Box sx={{ width: '100%' }}>
                            <ReactJson
                              src={item}
                              theme="monokai"
                              collapsed={false}
                              displayDataTypes={false}
                              displayObjectSize={false}
                              style={{ backgroundColor: '#1e1e1e', width: '100%' }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {endpoint === '/devinfos' && devinfoExpanded === index && (
                      <TableRow>
                        <TableCell colSpan={deleteEnabled ? 11 : 9} sx={{ backgroundColor: '#1e1e1e', padding: '16px', width: '100%' }}>
                          <Typography variant="h6" gutterBottom color="white">
                            DevInfo JSON
                          </Typography>
                          <Box sx={{ width: '100%' }}>
                            <ReactJson
                              src={devinfoContents}
                              theme="monokai"
                              collapsed={false}
                              displayDataTypes={false}
                              displayObjectSize={false}
                              style={{ backgroundColor: '#1e1e1e', width: '100%' }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {endpoint !== '/protocols' && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={nextPageToken ? -1 : totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              nextIconButtonProps={{
                disabled: !nextPageToken && page >= Math.ceil(totalCount / rowsPerPage) - 1,
              }}
            />
          )}
        </>
      )}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure? This is permanent and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default FdiRepositoryExplorer;